import { Component, OnInit, OnDestroy, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { NotificationService } from 'src/app/_services/common/notification.service';
import { CaptureElementToPdfDataUri } from 'src/util/generic-functions';
import { Claims } from 'src/app/_models/common/claims';
import { ApiRequest, BillingCycleEnum } from 'src/app/_models';
import { environment } from 'src/environments/environment';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { FunctionDetail } from 'src/app/_models/billing-pricing/FunctionDetail';
import { StripeSessionRequest, IStripeSessionRequest } from 'src/app/_models/stripe/stripe';
import { ClaimsService } from 'src/app/_services/common/claims.service';
import { OrgBasicFunctionSubscriptionService } from 'src/app/_services/billing-pricing/Org-BasicFunction-Subscription.service';
import { OrgSubscriptionFunctionService } from 'src/app/_services/billing-pricing/Org-Subscription-Function.service';
import { StripeService } from 'src/app/_services/stripe/stripe.service';
import { EssentialPackService } from 'src/app/_services/essential-pack/essential-pack.service';
import { PurchaseOrderService } from 'src/app/_services/purchase-order/purchase-order.service';
import { BandPlanResponse } from 'src/app/_models/essential-pack/essential-pack';
import { PurchaseOrderReceiptStore } from '../purchase-order-activation-panel/purchase-order-activation.store';

declare var Stripe: any;
import { ICONS } from 'src/app/_helpers/icons.constants';
import { ButtonComponent } from 'src/app/shared/design-system/button/button.component';
import { PanelComponent } from 'src/app/shared/design-system/panel/panel.component';
import { ButtonConfig } from 'src/app/shared/design-system/button/button.modal';
import { PanelConfig } from 'src/app/shared/design-system/panel/panel.modal';
import { TextboxComponent } from 'src/app/shared/design-system/textbox/textbox.component';
import { TextboxConfig } from 'src/app/shared/design-system/textbox/textbox.modal';
import { CheckboxComponent } from 'src/app/shared/design-system/checkbox/checkbox.component';
import { CheckboxConfig } from 'src/app/shared/design-system/checkbox/checkbox.modal';
import { ModalComponent } from 'src/app/shared/design-system/modal/modal.component';
import { ModalConfig } from 'src/app/shared/design-system/modal/modal.modal';
import { termsOfServiceURl } from 'src/app/common/commonvariables';

/** Mirrors backend Solo.Enum OrgPurchaseOrderStatus (Sent = 1, Recalled = 2). */
enum OrgPurchaseOrderStatus {
  Sent = 1,
  Recalled = 2,
}

@Component({
  selector: 'purchase-order-panel',
  templateUrl: './purchase-order-panel.component.html',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonComponent, PanelComponent, TextboxComponent, CheckboxComponent, ModalComponent],
})
export class PurchaseOrderPanelComponent implements OnInit, OnDestroy {
  @Output() closePanelEvent = new EventEmitter<boolean>();

  @ViewChild('poCaptureArea') poCaptureArea: ElementRef<HTMLElement>;
  @ViewChild('implementationCaptureArea') implementationCaptureArea: ElementRef<HTMLElement>;
  private captureTimer: any = null;
  private capturePromise: Promise<void> | null = null;

  /** Set when the sales team recalls this PO in real time (SignalR) -> blur the panel. */
  isWithdrawn: boolean = false;
  private notificationSubscription: Subscription;

  public readonly ICONS = ICONS;
  termsOfServiceUrl = termsOfServiceURl;

  claims!: Claims;

  // ----- Purchase order data -----
  orgPurchaseOrder: any;
  bandPlanDetails: BandPlanResponse;
  paymentCurrencyId: number = 0;
  paymentCurrencyCode: string = '';
  isYearlyPlan: boolean = false;
  casualUserBasicPrice: number = 0;
  powerUserBasicPrice: number = 0;
  noOfUsers: number = 0;
  numberOfPowerUsers: number = 0;
  implementationCharge: number = 0;

  // ----- Acceptance state -----
  isActivationAcknowledged: boolean = false;
  isQuoteAccepted: boolean = false;
  isTermsAgree: boolean = false;

  // ----- Reusable design-system configs -----
  panelConfig: PanelConfig = {
    id: 'purchase-order-panel',
    title: '',
    size: 'xl',
    position: 'right',
    hasOverlay: true,
    projectedContent: true,
    hideCloseIcon: true,
    hideScrollButtons: false,
  };

  preparedByConfig: TextboxConfig = { id: 'prepared-by', label: '', readonly: true, value: '' };
  emailConfig: TextboxConfig = { id: 'prepared-email', label: '', readonly: true, value: '' };
  dateConfig: TextboxConfig = { id: 'prepared-date', label: '', readonly: true, value: '' };
  planTypeConfig: TextboxConfig = { id: 'plan-type', label: '', readonly: true, value: '' };
  planPriceConfig: TextboxConfig = { id: 'plan-price', label: '', readonly: true, currencyText: '', value: '' };
  numberOfUsersConfig: TextboxConfig = { id: 'number-of-users', label: '', readonly: true, value: '' };
  numberOfPowerUsersConfig: TextboxConfig = { id: 'number-of-power-users', label: '', readonly: true, value: '' };
  accessChargeConfig: TextboxConfig = { id: 'access-charge', label: '', readonly: true, currencyText: '', value: '' };
  implementationCostConfig: TextboxConfig = { id: 'implementation-cost', label: '', readonly: true, currencyText: '', value: '' };

  activationAckCheckboxConfig: CheckboxConfig = {
    id: 'activation-ack-checkbox',
    label: '',
    ariaLabel: '',
    color: 'primary',
    size: 'md',
  };

  acceptQuoteCheckboxConfig: CheckboxConfig = {
    id: 'accept-quote-checkbox',
    label: '',
    ariaLabel: '',
    color: 'primary',
    size: 'md',
  };

  termsCheckboxConfig: CheckboxConfig = {
    id: 'terms-checkbox',
    label: '',
    ariaLabel: '',
    color: 'primary',
    size: 'md',
  };

  termsOfServiceButtonConfig: ButtonConfig = {
    id: 'terms-of-service-btn',
    label: '',
    color: 'primary-tertiary',
    size: 'sm',
    icon: ICONS.EXTERNAL_LINK,
    iconPosition: 'left',
    ariaLabel: '',
  };

  submitButtonConfig: ButtonConfig = {
    id: 'accept-proceed-btn',
    label: '',
    color: 'primary',
    size: 'lg',
    disabled: true,
    ariaLabel: '',
  };

  // ----- One-time implementation charge popup -----
  isImplementationTermsAgreed: boolean = false;
  /** Re-entry guard for Accept: blocks repeated clicks during the (async) receipt capture. */
  isAccepting: boolean = false;
  /** Re-entry guard for Proceed to Payment: blocks repeated clicks during capture + Stripe session. */
  isProceedingToPayment: boolean = false;

  implementationModalConfig: ModalConfig = {
    id: 'implementation-charge-modal',
    visible: false,
    title: '',
    size: 'md',
    variant: 'default',
    hiddenCloseIcon: true,
  };

  implementationTermsCheckboxConfig: CheckboxConfig = {
    id: 'implementation-terms-checkbox',
    label: '',
    ariaLabel: '',
    color: 'primary',
    size: 'md',
  };

  proceedPaymentButtonConfig: ButtonConfig = {
    id: 'proceed-payment-btn',
    label: '',
    color: 'primary',
    size: 'lg',
    disabled: true,
    ariaLabel: '',
    block: true,
  };

  stripeSessionId: any;

  constructor(
    private claimsService: ClaimsService,
    private router: Router,
    private essentialPackService: EssentialPackService,
    private purchaseOrderService: PurchaseOrderService,
    private orgBasicFunctionSubscriptionService: OrgBasicFunctionSubscriptionService,
    private orgSubscriptionFunctionService: OrgSubscriptionFunctionService,
    private stripeService: StripeService,
    private toastr: ToastrService,
    private ngxService: NgxUiLoaderService,
    private receiptStore: PurchaseOrderReceiptStore,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private elRef: ElementRef
  ) {
    this.claimsService.currentClaims.subscribe((claims) => {
      this.claims = claims;
    });
  }

  ngOnInit(): void {
    this.fnHideTidioChat();
    this.fnInitTranslations();
    this.fnGetOrgBasicFunctionSubscriptionTrialDetails();
    this.fnGetOrgPurchaseOrderDetails();
    this.fnLoadStripe();
    this.fnListenForPurchaseOrderWithdrawn();
  }

  /**
   * Listen for the real-time PO signals raised by sales:
   *  - `po_withdrawn` (recall) -> blur the open panel.
   *  - `po_sent` (re-submit after a recall) -> clear the blur and reload the updated PO so the
   *    logged-in customer immediately sees the refreshed terms without a manual refresh.
   */
  private fnListenForPurchaseOrderWithdrawn(): void {
    this.notificationSubscription = this.notificationService
      .onNotificationRaised()
      .subscribe((notification) => {
        const key = notification?.text?.key;
        if (key === 'po_withdrawn') {
          this.isWithdrawn = true;
        } else if (key === 'po_sent') {
          this.isWithdrawn = false;
          this.fnGetOrgPurchaseOrderDetails();
        }
      });
  }

  ngOnDestroy(): void {
    this.notificationSubscription?.unsubscribe();
  }
    fnHideTidioChat(){
    if (window.tidioChatApi) {
      window.tidioChatApi.hide();
    }
  }

  /** Populate the static design-system config labels from the translator. */
  private fnInitTranslations(): void {
    this.panelConfig = { ...this.panelConfig, title: this.translate.instant('new_onb_po_panel_title') };
    this.preparedByConfig = { ...this.preparedByConfig, label: this.translate.instant('new_onb_prepared_by') };
    this.emailConfig = { ...this.emailConfig, label: this.translate.instant('new_onb_email') };
    this.dateConfig = { ...this.dateConfig, label: this.translate.instant('new_onb_date') };
    this.planTypeConfig = { ...this.planTypeConfig, label: this.translate.instant('new_onb_plan_type') };
    this.planPriceConfig = { ...this.planPriceConfig, label: this.translate.instant('new_onb_plan_price') };
    this.numberOfUsersConfig = { ...this.numberOfUsersConfig, label: this.translate.instant('new_onb_number_of_users') };
    this.numberOfPowerUsersConfig = { ...this.numberOfPowerUsersConfig, label: this.translate.instant('new_onb_number_of_power_users') };
    this.accessChargeConfig = { ...this.accessChargeConfig, label: this.translate.instant('new_onb_po_access_charge') };
    this.implementationCostConfig = { ...this.implementationCostConfig, label: this.translate.instant('new_onb_po_impl_cost') };
    this.activationAckCheckboxConfig = {
      ...this.activationAckCheckboxConfig,
      label: this.translate.instant('new_onb_activation_ack_label'),
      ariaLabel: this.translate.instant('new_onb_activation_ack_aria'),
    };
    // label is set with the organisation name in fnBuildConfigs once the PO loads.
    this.acceptQuoteCheckboxConfig = {
      ...this.acceptQuoteCheckboxConfig,
      ariaLabel: this.translate.instant('new_onb_accept_quote_aria'),
    };
    this.termsCheckboxConfig = {
      ...this.termsCheckboxConfig,
      label: this.translate.instant('new_onb_terms_confirm_label'),
      ariaLabel: this.translate.instant('new_onb_terms_confirm_aria'),
    };
    this.termsOfServiceButtonConfig = {
      ...this.termsOfServiceButtonConfig,
      label: this.translate.instant('new_onb_eod_terms_of_service'),
      ariaLabel: this.translate.instant('new_onb_eod_terms_of_service'),
    };
    this.submitButtonConfig = {
      ...this.submitButtonConfig,
      label: this.translate.instant('new_onb_accept_proceed'),
      ariaLabel: this.translate.instant('new_onb_accept_proceed'),
    };
    this.implementationTermsCheckboxConfig = {
      ...this.implementationTermsCheckboxConfig,
      label: this.translate.instant('new_onb_impl_terms_label'),
      ariaLabel: this.translate.instant('new_onb_impl_terms_aria'),
    };
    this.proceedPaymentButtonConfig = {
      ...this.proceedPaymentButtonConfig,
      label: this.translate.instant('new_onb_proceed_for_payment'),
      ariaLabel: this.translate.instant('new_onb_proceed_for_payment'),
    };
  }

  /** Per-user plan price for the saved billing cycle. */
  get planPrice(): number {
    if (!this.bandPlanDetails) {
      return 0;
    }
    return this.isYearlyPlan ? this.bandPlanDetails.yearlyAmount : this.bandPlanDetails.amount;
  }

  /** Minimum billing: access charge x users + power users x plan price. */
  get minimumBilling(): number {
    return this.casualUserBasicPrice * this.noOfUsers + this.numberOfPowerUsers * this.planPrice;
  }

  /** True when the sales team attached a note to the purchase order. */
  get hasNotes(): boolean {
    return !!(this.orgPurchaseOrder?.notes && String(this.orgPurchaseOrder.notes).trim());
  }

  /**
   * Accept button is enabled once the acceptance checkboxes are ticked. When the PO has a
   * note, the extra activation-acknowledgement checkbox must also be ticked.
   */
  get canAccept(): boolean {
    return this.isQuoteAccepted && this.isTermsAgree && (!this.hasNotes || this.isActivationAcknowledged);
  }

  fnGetOrgPurchaseOrderDetails(): void {
    this.purchaseOrderService.GetOrgPurchaseOrderDetails(this.claims.OrgId).subscribe((res: any) => {
      if (res && res.responseInfo) {
        this.orgPurchaseOrder = res.responseInfo;
        // Persist the withdrawn state across refreshes: if the PO is already recalled (status 2),
        // show the blur overlay on load (not only when the real-time po_withdrawn event fires).
        if (this.orgPurchaseOrder.status === OrgPurchaseOrderStatus.Recalled) {
          this.isWithdrawn = true;
        }
        this.noOfUsers = this.orgPurchaseOrder.noOfUsers;
        this.numberOfPowerUsers = this.orgPurchaseOrder.numberOfPowerUsers;
        this.implementationCharge = this.orgPurchaseOrder.implementationCharge;
        this.isYearlyPlan = this.orgPurchaseOrder.billingCycle === BillingCycleEnum.Yearly;
        this.fnGetOrganisationPaymentCurrency();
        this.fnBuildConfigs();
      }
    });
  }

  /** Resolve the organisation's payment currency (id + code), then load the band plan. */
  fnGetOrganisationPaymentCurrency(): void {
    let request = new ApiRequest<FunctionDetail>(
      'SoloGetOrganisationPaymentCurrency',
      environment.applicationVersion,
      environment.requestId
    );
  
    let currencyId = this.claims.CurrentOrganisationCurrencyId 
     ? this.claims.CurrentOrganisationCurrencyId 
     : parseInt(this.claims.SelectedCurrency);

    request.requestInfo = new FunctionDetail(0, currencyId);
    this.orgSubscriptionFunctionService.getOrganisationPaymentCurrency(request).subscribe((data: any) => {
      if (data.statusCode == 0 && data.responseInfo) {
        this.paymentCurrencyId = data.responseInfo.currencyId;
        this.paymentCurrencyCode = data.responseInfo.currencyName;
      }
      this.fnGetBandPlanById(this.orgPurchaseOrder?.bandPlanId);
      this.fnBuildConfigs();
    });
  }

  fnGetBandPlanById(bandPlanId: number): void {
    if (!bandPlanId) {
      return;
    }
    this.essentialPackService.GetBandPlanById(bandPlanId).subscribe((response: any) => {
      if (response && response.responseInfo) {
        this.bandPlanDetails = response.responseInfo;
        this.fnMapBandDetails();
        this.fnBuildConfigs();
      }
    });
  }

  fnMapBandDetails(): void {
    if (this.bandPlanDetails) {
      let priceDetails = this.bandPlanDetails.priceList.find((p) => p.currencyId == this.paymentCurrencyId);
      this.bandPlanDetails.amount = priceDetails?.amount ? priceDetails.amount : 0;
      this.bandPlanDetails.yearlyAmount = priceDetails?.yearlyAmount ? priceDetails.yearlyAmount : 0;
      this.bandPlanDetails.currencySymbol = priceDetails?.currencySymbol;
      this.bandPlanDetails.currencyCode = priceDetails?.currencyCode;
    }
  }

  fnGetOrgBasicFunctionSubscriptionTrialDetails(): void {
    this.orgBasicFunctionSubscriptionService
      .GetOrgBasicFunctionSubscriptionTrialDetails(this.claims.OrgId)
      .subscribe((res: any) => {
        if (res.responseInfo != null) {
          this.powerUserBasicPrice = res.responseInfo.powerUserBasicPrice;
          this.casualUserBasicPrice = res.responseInfo.casualUserBasicPrice;
          this.fnBuildConfigs();
        }
      });
  }

  /** Refresh the readonly textbox configs from the current data (new objects to trigger CD). */
  fnBuildConfigs(): void {
    const preparedByName = this.orgPurchaseOrder?.preparedByName ?? '';
    const email = this.orgPurchaseOrder?.requestedByEmail ?? this.claims?.EmailId ?? '';
    const createdOn = this.orgPurchaseOrder?.createdOn ? new Date(this.orgPurchaseOrder.createdOn) : null;

    this.preparedByConfig = { ...this.preparedByConfig, value: preparedByName };
    this.emailConfig = { ...this.emailConfig, value: email };
    this.dateConfig = { ...this.dateConfig, value: createdOn ? formatDate(createdOn, 'dd-MMM-yyyy', 'en-US') : '' };
    this.planTypeConfig = { ...this.planTypeConfig, value: this.translate.instant(this.isYearlyPlan ? 'new_onb_yearly' : 'new_onb_monthly') };
    this.planPriceConfig = {
      ...this.planPriceConfig,
      label: this.translate.instant('new_onb_plan_price'),
      currencyText: this.paymentCurrencyCode,
      value: this.planPrice.toFixed(2),
    };
    this.numberOfUsersConfig = { ...this.numberOfUsersConfig, value: String(this.noOfUsers) };
    this.numberOfPowerUsersConfig = { ...this.numberOfPowerUsersConfig, value: String(this.numberOfPowerUsers) };
    this.accessChargeConfig = { ...this.accessChargeConfig, currencyText: this.paymentCurrencyCode, value: this.casualUserBasicPrice.toFixed(2) };
    this.implementationCostConfig = { ...this.implementationCostConfig, currencyText: this.paymentCurrencyCode, value: this.implementationCharge.toFixed(2) };
    const orgName = this.orgPurchaseOrder?.organisationName || this.claims?.OrgName || this.translate.instant('new_onb_your_organisation');
    this.acceptQuoteCheckboxConfig = {
      ...this.acceptQuoteCheckboxConfig,
      isHtmlLabel: true,
      label: this.translate.instant('new_onb_accept_quote_label', { org: (orgName || '').toUpperCase() }),
    };
    this.implementationTermsCheckboxConfig = {
      ...this.implementationTermsCheckboxConfig,
      isHtmlLabel: true,
      label: this.translate.instant('new_onb_impl_terms_label_html', { org: (orgName || '').toUpperCase() }),
    };
    if (this.hasNotes) {
      // Show the sales note as the acknowledgement checkbox label so the prospect confirms it.
      const noteHtml = String(this.orgPurchaseOrder.notes).replace(/\n/g, '<br>');
      this.activationAckCheckboxConfig = {
        ...this.activationAckCheckboxConfig,
        isHtmlLabel: true,
        label: noteHtml,
      };
    }

    // Data changed -> (re)schedule the silent background capture so the PDFs are ready
    // by the time the user accepts, without blocking the click.
    this.fnScheduleBackgroundCapture();
  }

  /** Debounced trigger: capture the receipts ~1s after the last data update settles. */
  private fnScheduleBackgroundCapture(): void {
    if (this.captureTimer) {
      clearTimeout(this.captureTimer);
    }
    this.captureTimer = setTimeout(() => {
      this.capturePromise = this.fnCaptureReceipts();
    }, 1000);
  }

  /** Silent on-load pre-warm: capture both receipts so later captures are fast. */
  private async fnCaptureReceipts(): Promise<void> {
    await this.fnCapturePoReceipt();
    await this.fnCaptureImplementationReceipt();
  }

  /** Capture the PO area (with the ticked acceptance checkboxes) to a PDF data-URI. */
  private async fnCapturePoReceipt(): Promise<void> {
    const poDataUri = await CaptureElementToPdfDataUri(this.poCaptureArea?.nativeElement, { title: this.translate.instant('new_onb_pdf_purchase_order_title') });
    if (poDataUri) {
      this.receiptStore.setPurchaseOrder(poDataUri);
    }
  }

  /** Capture the off-screen Implementation Agreement (with the agreed terms checkbox) to a PDF data-URI. */
  private async fnCaptureImplementationReceipt(): Promise<void> {
    const implDataUri = await CaptureElementToPdfDataUri(this.implementationCaptureArea?.nativeElement, { title: this.translate.instant('new_onb_pdf_implementation_agreement_title') });
    if (implDataUri) {
      this.receiptStore.setImplementationAgreement(implDataUri);
    }
  }


  onActivationAckChange(checked: boolean): void {
    this.isActivationAcknowledged = checked;
    // Reflect the ticked state on the config (new reference) so the checkbox renders as
    // CHECKED on the captured PO receipt, not just in the live (clicked) input.
    this.activationAckCheckboxConfig = { ...this.activationAckCheckboxConfig, isSelected: checked };
    this.fnUpdateSubmitState();
  }

  onAcceptQuoteChange(checked: boolean): void {
    this.isQuoteAccepted = checked;
    this.acceptQuoteCheckboxConfig = { ...this.acceptQuoteCheckboxConfig, isSelected: checked };
    this.fnUpdateSubmitState();
  }

  onTermsChange(checked: boolean): void {
    this.isTermsAgree = checked;
    this.termsCheckboxConfig = { ...this.termsCheckboxConfig, isSelected: checked };
    this.fnUpdateSubmitState();
  }

  private fnUpdateSubmitState(): void {
    this.submitButtonConfig = { ...this.submitButtonConfig, disabled: !this.canAccept };
  }

  fnOpenTermsOfService(): void {
    window.open(this.termsOfServiceUrl, '_blank');
  }

  /** Accept the purchase order -> show the one-time implementation charge popup. */
  async fnAcceptAndProceed(): Promise<void> {
    // Guard the click-gap: the receipt capture below is async, so without this the user
    // could click Accept several times before the popup appears. Block re-entry, disable
    // the button and show the blocking loader immediately so the UI is not interactive.
    if (!this.canAccept || this.isAccepting) {
      return;
    }
    this.isAccepting = true;
    this.submitButtonConfig = { ...this.submitButtonConfig, disabled: true };
    this.ngxService.start();
    try {
      // Capture the PO receipt now (with the ticked acceptance checkboxes) BEFORE the panel is
      // hidden — opening the implementation modal removes the panel (and its #poCaptureArea) so only
      // the dialog is visible. This state is transient: a refresh (without accepting) shows the panel again.
      if (this.captureTimer) {
        clearTimeout(this.captureTimer);
        this.captureTimer = null;
      }
      await this.fnCapturePoReceipt();
      // Open the implementation charge popup (this hides the panel + Accept button).
      this.implementationModalConfig = { ...this.implementationModalConfig, visible: true };
    } finally {
      this.ngxService.stop();
      this.isAccepting = false;
      this.fnUpdateSubmitState();
    }
  }

  onImplementationTermsChange(checked: boolean): void {
    this.isImplementationTermsAgreed = checked;
    // Reflect the ticked state on the shared config with a NEW reference so the off-screen
    // capture copy (which only re-renders via ngOnChanges) shows the checkbox as CHECKED on
    // the captured Implementation Agreement receipt — not unchecked.
    this.implementationTermsCheckboxConfig = { ...this.implementationTermsCheckboxConfig, isSelected: checked };
    this.proceedPaymentButtonConfig = { ...this.proceedPaymentButtonConfig, disabled: !checked };
  }

  onImplementationModalVisibleChange(visible: boolean): void {
    this.implementationModalConfig = { ...this.implementationModalConfig, visible };
  }

  async fnProceedToPayment(): Promise<void> {
    // Guard the click-gap: block re-entry, disable the button and start the blocking loader
    // IMMEDIATELY (before the async receipt capture) so the payment step can't be triggered
    // multiple times while the capture + Stripe session are being prepared.
    if (!this.isImplementationTermsAgreed || this.isProceedingToPayment) {
      return;
    }
    this.isProceedingToPayment = true;
    this.proceedPaymentButtonConfig = { ...this.proceedPaymentButtonConfig, disabled: true };
    this.ngxService.start();
    // Capture the Implementation Agreement now (with the agreed implementation-terms checkbox).
    // The PO receipt was already captured on accept, before the panel was hidden.
    await this.fnCaptureImplementationReceipt();
    // First-time card capture -> always collect the card via Stripe, returning to the activation page.
    // The loader stays up until Stripe redirects; it is released (and the button re-enabled) only if
    // the redirect fails (fnStripeRedirectFailed).
    this.fnLoadStripeSession();
  }

  private fnLoadStripeSession(): void {
    const URL = environment.AppUrl + 'purchase-order-activation';
    const CANCELURL = environment.AppUrl + 'dashboard/overview';
    const userEmail = this.claims.EmailId && this.claims.EmailId != '' ? this.claims.EmailId : this.claims.LoginId;
    const sessionRequestData = new StripeSessionRequest(URL, userEmail, this.paymentCurrencyCode, CANCELURL);
    const sessionRequest = new ApiRequest<IStripeSessionRequest>(
      'CreateStripeSession',
      environment.applicationVersion,
      environment.requestId
    );
    sessionRequest.requestInfo = sessionRequestData;
    this.stripeService.CreateStripeSession(sessionRequest).subscribe(
      (sessionData: any) => {
        if (sessionData.statusCode == 0 && sessionData.responseInfo) {
          this.stripeSessionId = sessionData.responseInfo;
          this.fnShowStripeCheckoutWindow();
        } else {
          this.fnStripeRedirectFailed(this.translate.instant('new_onb_payment_start_failed'));
        }
      },
      () => this.fnStripeRedirectFailed(this.translate.instant('new_onb_payment_start_failed'))
    );
  }

  private fnShowStripeCheckoutWindow(): void {
    const stripe = Stripe(environment.StripePublicKey);
    stripe.redirectToCheckout({ sessionId: this.stripeSessionId }).then((result: any) => {
      // On success the browser navigates to Stripe (page unloads); only handle failure here.
      if (result?.error) {
        this.fnStripeRedirectFailed(result.error.message);
      }
    });
  }

  /** Redirect to Stripe failed -> release the loader and re-enable the button so the user can retry. */
  private fnStripeRedirectFailed(message: string): void {
    this.ngxService.stop();
    this.isProceedingToPayment = false;
    this.proceedPaymentButtonConfig = { ...this.proceedPaymentButtonConfig, disabled: false };
    this.toastr.error(message);
  }

  private fnLoadStripe(): void {
    const stripeImport = document.createElement('script');
    stripeImport.type = 'text/javascript';
    stripeImport.src = 'https://js.stripe.com/v3/';
    this.elRef.nativeElement.appendChild(stripeImport);
  }

  fnClosePanel(): void {
    this.closePanelEvent.emit(true);
  }
}
